import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux';
import {
  setAnnexIVFieldsState,
  setEUDeclarationFieldsState,
  setFRIAFieldsState,
  setDocuments,
  setDocumentsLoading,
  setCreateAnnexIVDraftResponse,
  setCreateEUDeclarationResponse,
  setCreateFRIAResponse,
  setEUDeclarationStatus,
  setDPIAFieldsState,
  setCreateDPIAResponse,
  setFRIAStatus
} from '../../../../store/slices/documentationSlice';
import { fetchRequirementStepsByAISystemId } from '../../../../store/slices/requirementStepsSlice';
import {
  AISuggestionIcon,
  DocumentCheckIcon,
  DownloadDocumentEditorIcon,
  XCloseModal
} from '../../../../icons/SvgIcons';
import { Divider } from '../../../ui/Divider/Divider';
import { ZoomContent } from '../../../ui/ZoomContent/ZoomContent';
import { AIProcessingLoader } from '../../../ui/AIProcessingLoader/AIProcessingLoader';
import { PrimaryButton } from '../../../ui/Button/Buttons';
import {
  generateAnnexIVContent,
  getAnnexIVByAiSystem,
  updateAnnexIV,
  completeAnnexIV
} from '../../../../services/AnnexIVService';
import {
  generateEUDeclarationContent,
  completeEUDeclaration,
  updateEUDeclaration,
  getEUDeclarationByAiSystem
} from '../../../../services/EUDeclarationService';
import {
  updateFRIA,
  completeFRIA,
  generateFRIAContent,
  getFRIAById,
  getFRIAByAiSystem
} from '../../../../services/FRIAService';
import { AnnexIV } from '../../Draft/AnnexIV/AnnexIV';
import {
  CONFORMITY_STATEMENTS_KEY,
  DeclarationOfConformity,
  HARMONISED_STANDARDS_KEY,
  PROVIDER_DECLARATION_CHECKED_KEY
} from '../../Draft/DeclarationOfConformity/DeclarationOfConformity';
import { FRIA } from '../../Draft/FRIA/FRIA';
import { FileViewer } from '../../FileViewer/FileViewer';
import {
  getDocumentation,
  signDocument
} from '../../../../services/DocumentationService';
import {
  buildAnnexIVPayload,
  buildDPIAPayload,
  buildEUDeclarationPayload,
  buildFRIAPayload,
  hasDynamicGroupContent,
  isFilledValue,
  mapAnnexContentToFields,
  mapEUDeclarationContentToFields,
  mergeFieldRecords,
  mapFRIAContentToFields,
  mapDPIAContentToFields
} from '../../../../utils/documentationHelpers';
import { documentationSections } from '../../Draft/AnnexIV/AnnexIV.consts';
import {
  conformityStatement,
  harmonisedStandards,
  notifiedBodyConformityAssessment,
  systemAndProviderInformation
} from '../../Draft/DeclarationOfConformity/DeclarationOfConformity.consts';
import {
  friaMetaInformation,
  systemDeployerInformation as friaSystemDeployerInformation,
  useContext,
  durationFrequencyOfUse,
  affectedPersonsGroups,
  humanOversight,
  mitigationCorrectiveMeasures,
  finalAssessmentDecision,
  ROLE_OF_SYSTEM_KEY,
  VULNERABLE_GROUPS_KEY,
  FUNDAMENTAL_RIGHTS_IN_SCOPE_KEY,
  RISK_SCENARIOS_KEY,
  OVERSIGHT_MODEL_KEY,
  OVERALL_RISK_MITIGATION_STATUS_KEY,
  OVERALL_DECISION_KEY
} from '../../Draft/FRIA/FRIA.consts';
import { DocumentEditorProps } from './DocumentEditor.types';
import { useParams } from 'react-router-dom';
import { DPIA } from '../../Draft/DPIA/DPIA';
import {
  completeDPIA,
  generateDPIAContent,
  getDPIAById,
  updateDPIA
} from '../../../../services/DPIAService';
import { Toast } from '../../../ui/Toast/Toast';
import { EMAIL_REGEX } from '../../../../constants/Validation.constants';
import { getAIJobStatus } from '../../../../services/AIJobService';
import { AIJobStatus } from '../../../../types/AIJob.types';
import { useAuthorization } from '../../../../hooks/useAuthorization';
import {
  consultationAndPriorConsultationFields,
  controllerAndAssessmentIdentificationFields,
  descriptionOfProcessingOperationsAndPurposesFields,
  DPIA_FINAL_DECISION_KEY,
  DPIA_OVERALL_ASSESSMENT_KEY,
  MEASURES_TO_ADDRESS_RISKS_KEY,
  necessityAndProportionalityFields,
  RISK_ASSESSMENT_AND_MITIGATION_MEASURES_KEY
} from '../../Draft/DPIA/DPIA.const';

const INITIAL_JOB_STATUS_DELAY_MS = 3000;
const GENERATION_POLL_INTERVAL_MS = 4000;
const MAX_GENERATION_ATTEMPTS = 60;

export function DocumentEditor({
  title,
  content,
  annexId,
  declarationId,
  friaId,
  dpiaId,
  onClose,
  showHeader = true,
  showFooter = true,
  showZoomControls = true,
  showDownloadButton = true,
  showCloseButton = true,
  className = ''
}: DocumentEditorProps) {
  const {
    annexIVFields,
    euDeclarationFields,
    dpiaFields,
    friaFields,
    previewDocument,
    documentationReqBody,
    createAnnexIVDraftResponse,
    createEUDeclarationResponse,
    createDPIAResponse,
    euDeclarationStatus,
    documents,
    createFRIAResponse,
    friaStatus
  } = useAppSelector((state) => state.documentation);

  const { openSidePanel } = useAppSelector((state) => state.sidePanel);

  const dispatch = useAppDispatch();
  const { can, roleResolved } = useAuthorization();

  const [showAISuggestion, setShowAISuggestion] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [generationStatus, setGenerationStatus] = useState<
    AIJobStatus | 'TIMEOUT' | null
  >(null);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [finalizingDraft, setFinalizingDraft] = useState(false);
  const [disableMarkAsFinalButton, setDisableMarkAsFinalButton] =
    useState(true);
  const [zoom, setZoom] = useState(100);
  const [showToast, setShowToast] = useState(false);
  const { id: systemId } = useParams<{ id: string }>();
  const canGenerateSuggestions =
    roleResolved && can('aiSystems.documentation.generate');
  const canFinalizeDocument =
    roleResolved && can('aiSystems.documentation.finalize');
  const [showPermissionToast, setShowPermissionToast] = useState(false);

  const isDraftDocument = (() => {
    if (content === 'DOCUMENT_PREVIEW') {
      return previewDocument?.isDraft === true;
    }

    const documentId = annexId
      ? createAnnexIVDraftResponse?.documentId
      : declarationId
        ? createEUDeclarationResponse?.documentId
        : friaId
          ? createFRIAResponse?.documentId
          : dpiaId
            ? createDPIAResponse?.documentId
            : undefined;

    if (!documentId) {
      return false;
    }

    return documents.some((doc) => doc.id === documentId && doc.isDraft);
  })();

  const refreshDocumentationList = useCallback(async () => {
    if (!documentationReqBody.aiSystemId) {
      return;
    }

    try {
      dispatch(setDocumentsLoading(true));
      const response = await getDocumentation(documentationReqBody);
      dispatch(setDocuments(response));
    } catch (error) {
      console.error('Failed to refresh documentation list:', error);
      dispatch(setDocumentsLoading(false));
    }
  }, [dispatch, documentationReqBody]);

  const aiSystemId = documentationReqBody.aiSystemId || systemId;

  const refreshEUDeclarationState = useCallback(async () => {
    if (!aiSystemId) {
      return null;
    }

    try {
      const latest = await getEUDeclarationByAiSystem(aiSystemId);
      dispatch(setCreateEUDeclarationResponse(latest));
      dispatch(
        setEUDeclarationFieldsState(mapEUDeclarationContentToFields(latest))
      );
      return latest;
    } catch (error) {
      console.error('Failed to refresh EU Declaration state:', error);
      return null;
    }
  }, [aiSystemId, dispatch]);

  const refreshFRIAState = useCallback(async () => {
    if (!aiSystemId) {
      return null;
    }

    try {
      const latest = await getFRIAByAiSystem(aiSystemId);
      dispatch(setCreateFRIAResponse(latest));
      dispatch(setFRIAFieldsState(mapFRIAContentToFields(latest)));
      return latest;
    } catch (error) {
      console.error('Failed to refresh FRIA state:', error);
      return null;
    }
  }, [aiSystemId, dispatch]);

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      setTimeout(resolve, ms);
    });

  const getErrorMessage = (error: unknown): string => {
    if (typeof error === 'object' && error !== null) {
      const candidate = error as {
        response?: { data?: { detail?: string; message?: string } };
        message?: string;
      };
      const detail = candidate.response?.data?.detail;
      if (detail) {
        return detail;
      }
      const message = candidate.response?.data?.message ?? candidate.message;
      if (message) {
        return message;
      }
    }
    return 'Unable to generate suggestions right now. Please try again.';
  };

  const refreshGeneratedDocumentState = useCallback(async () => {
    if (annexId) {
      if (!aiSystemId) {
        console.warn(
          '[refreshGeneratedDocumentState] aiSystemId is empty, skipping Annex IV refresh'
        );
        return;
      }
      const latest = await getAnnexIVByAiSystem(aiSystemId);
      const mapped = mapAnnexContentToFields(latest);
      dispatch(setCreateAnnexIVDraftResponse(latest));
      dispatch(setAnnexIVFieldsState(mapped));
      return;
    }

    if (declarationId) {
      await refreshEUDeclarationState();
      return;
    }

    if (friaId) {
      const latest = await getFRIAById(friaId);
      const mapped = mapFRIAContentToFields(latest);
      dispatch(setCreateFRIAResponse(latest));
      dispatch(setFRIAFieldsState(mapped));
      return;
    }

    if (dpiaId) {
      const latest = await getDPIAById(dpiaId);
      const mapped = mapDPIAContentToFields(latest);
      dispatch(setCreateDPIAResponse(latest));
      dispatch(setDPIAFieldsState(mergeFieldRecords(dpiaFields, mapped)));
      return;
    }

    console.warn(
      '[refreshGeneratedDocumentState] No document ID matched — nothing refreshed'
    );
  }, [
    annexId,
    declarationId,
    friaId,
    dpiaId,
    aiSystemId,
    dispatch,
    dpiaFields,
    refreshEUDeclarationState
  ]);

  const pollGenerationJob = useCallback(
    async (
      jobId: string,
      options?: { initialDelayMs?: number }
    ): Promise<'COMPLETED' | 'FAILED' | 'TIMEOUT'> => {
      const initialDelayMs = options?.initialDelayMs ?? 0;
      if (initialDelayMs > 0) {
        await sleep(initialDelayMs);
      }

      for (let attempt = 0; attempt < MAX_GENERATION_ATTEMPTS; attempt += 1) {
        try {
          const job = await getAIJobStatus(jobId);
          setGenerationStatus(job.status);

          if (job.status === 'COMPLETED') {
            await refreshGeneratedDocumentState();
            setGenerationError(null);
            return 'COMPLETED';
          }

          if (job.status === 'FAILED') {
            setGenerationError(
              job.error ?? 'Generation failed. Please retry the suggestion.'
            );
            return 'FAILED';
          }
        } catch (error) {
          const message = getErrorMessage(error);
          setGenerationError(
            message === 'Job not found'
              ? 'Generation job was not found or expired. Please retry.'
              : message
          );
          setGenerationStatus('FAILED');
          return 'FAILED';
        }

        if (attempt < MAX_GENERATION_ATTEMPTS - 1) {
          await sleep(GENERATION_POLL_INTERVAL_MS);
        }
      }

      setGenerationStatus('TIMEOUT');
      setGenerationError(null);
      return 'TIMEOUT';
    },
    [refreshGeneratedDocumentState]
  );

  const handleSuggestContent = async () => {
    if (!canGenerateSuggestions) {
      return;
    }

    if (!annexId && !declarationId && !friaId && !dpiaId) {
      console.error(
        'Either Annex ID, Declaration ID, FRIA ID, or DPIA ID is required to suggest content'
      );
      return;
    }

    try {
      setIsGenerating(true);
      setGenerationError(null);
      setGenerationStatus(null);
      setCurrentJobId(null);

      if (typeof persistDraftRef.current === 'function') {
        await persistDraftRef.current();
      }

      let jobId: string | null = null;

      if (annexId) {
        const response = await generateAnnexIVContent(annexId);
        jobId = response.jobId;
        setGenerationStatus(response.status);
      } else if (declarationId) {
        const response = await generateEUDeclarationContent(declarationId);
        jobId = response.jobId;
        setGenerationStatus(response.status);
      } else if (dpiaId) {
        const response = await generateDPIAContent(dpiaId);
        jobId = response.jobId;
        setGenerationStatus(response.status);
      } else if (friaId) {
        const response = await generateFRIAContent(friaId);
        jobId = response.jobId;
        setGenerationStatus(response.status);
      }

      if (!jobId) {
        throw new Error('Missing generation job identifier');
      }

      setCurrentJobId(jobId);
      const finalStatus = await pollGenerationJob(jobId, {
        initialDelayMs: INITIAL_JOB_STATUS_DELAY_MS
      });

      if (finalStatus === 'COMPLETED') {
        setShowToast(true);
      }
    } catch (error) {
      setGenerationStatus('FAILED');
      setGenerationError(getErrorMessage(error));
      console.error('Failed to suggest content:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleContinueChecking = async () => {
    if (!currentJobId || isGenerating) {
      return;
    }

    try {
      setIsGenerating(true);
      setGenerationError(null);
      await pollGenerationJob(currentJobId);
    } finally {
      setIsGenerating(false);
    }
  };

  //draft saving/updating
  const persistDraft = useCallback(async () => {
    if (
      content === 'DOCUMENT_PREVIEW' ||
      (!annexId && !declarationId && !friaId && !dpiaId)
    ) {
      return;
    }

    if (annexId) {
      const payload = buildAnnexIVPayload(
        annexIVFields,
        createAnnexIVDraftResponse ?? undefined
      );
      const updated = await updateAnnexIV(annexId, payload);
      dispatch(setCreateAnnexIVDraftResponse(updated));
      dispatch(setAnnexIVFieldsState(mapAnnexContentToFields(updated)));
    } else if (declarationId) {
      if (euDeclarationStatus === 'COMPLETED') {
        return;
      }
      const payload = buildEUDeclarationPayload(
        euDeclarationFields,
        createEUDeclarationResponse
      );
      if (!payload) {
        throw new Error('Missing EU Declaration metadata for update');
      }
      await updateEUDeclaration(declarationId, payload);
      await refreshEUDeclarationState();
    } else if (dpiaId) {
      const payload = buildDPIAPayload(dpiaFields, createDPIAResponse);
      if (payload) {
        const updated = await updateDPIA(dpiaId, payload);
        dispatch(setCreateDPIAResponse(updated));
        dispatch(
          setDPIAFieldsState(
            mergeFieldRecords(dpiaFields, mapDPIAContentToFields(updated))
          )
        );
      }
    } else if (friaId) {
      if (friaStatus === 'complete') {
        return;
      }
      const payload = buildFRIAPayload(friaFields, createFRIAResponse);
      const updated = await updateFRIA(friaId, payload);
      dispatch(setCreateFRIAResponse(updated));
      dispatch(setFRIAFieldsState(mapFRIAContentToFields(updated)));
    }

    await refreshDocumentationList();
    if (systemId && !openSidePanel) {
      dispatch(fetchRequirementStepsByAISystemId(systemId));
    }
  }, [
    annexId,
    declarationId,
    dpiaId,
    content,
    annexIVFields,
    euDeclarationFields,
    dpiaFields,
    createAnnexIVDraftResponse,
    createEUDeclarationResponse,
    createDPIAResponse,
    friaId,
    content,
    annexIVFields,
    euDeclarationFields,
    friaFields,
    createAnnexIVDraftResponse,
    createEUDeclarationResponse,
    createFRIAResponse,
    euDeclarationStatus,
    friaStatus,
    dispatch,
    refreshDocumentationList,
    refreshEUDeclarationState,
    systemId,
    openSidePanel
  ]);

  const persistDraftRef = useRef(persistDraft);

  useEffect(() => {
    persistDraftRef.current = persistDraft;
  }, [persistDraft]);

  const handleSaveDraft = async () => {
    if (!annexId && !declarationId && !dpiaId && !friaId) {
      return;
    }

    try {
      setSavingDraft(true);
      await persistDraft();
    } catch (error) {
      console.error('Failed to save draft:', error);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleClose = async () => {
    try {
      await persistDraft();
    } catch (error) {
      console.error('Failed to save draft:', error);
    } finally {
      onClose?.();
    }
  };

  //finalizacija
  const handleMarkAsFinal = async () => {
    if (!canFinalizeDocument) {
      setShowPermissionToast(true);
      return;
    }

    if (!annexId && !declarationId && !dpiaId && !friaId) {
      console.error(
        'Either Annex ID, Declaration ID, FRIA ID, or DPIA ID is required to mark as final'
      );
      return;
    }

    try {
      setFinalizingDraft(true);
      if (annexId) {
        const payload = buildAnnexIVPayload(
          annexIVFields,
          createAnnexIVDraftResponse ?? undefined
        );
        const result = await completeAnnexIV(annexId, payload);

        if (!result.success) {
          console.error(
            'Annex IV is missing mandatory fields:',
            result.missingFields
          );
          return;
        }

        const documentId = createAnnexIVDraftResponse?.documentId;
        if (!documentId) {
          throw new Error('Missing Annex IV document identifier for signing');
        }

        if (result.requiresSignature) {
          const returnUrl = `${window.location.origin}/ai-systems/${systemId}/documentation`;
          const signingUrl = await signDocument({ documentId, returnUrl });
          window.location.assign(signingUrl);
        } else {
          // SignatureAPI disabled - generate PDF and finalize
          await signDocument({ documentId, returnUrl: window.location.href });
        }
      } else if (declarationId) {
        const payload = buildEUDeclarationPayload(
          euDeclarationFields,
          createEUDeclarationResponse
        );
        if (!payload) {
          throw new Error('Missing EU Declaration metadata for completion');
        }
        const result = await completeEUDeclaration(declarationId, payload);

        if (!result.success) {
          console.error(
            'EU Declaration is missing mandatory fields:',
            result.missingFields
          );
          return;
        }

        dispatch(setEUDeclarationStatus('COMPLETED'));
        await refreshEUDeclarationState();

        const documentId = createEUDeclarationResponse?.documentId;
        if (!documentId) {
          throw new Error('Missing document identifier for signing');
        }

        if (result.requiresSignature) {
          const returnUrl = `${window.location.origin}/ai-systems/${systemId}/documentation`;
          const signingUrl = await signDocument({ documentId, returnUrl });
          window.location.assign(signingUrl);
        } else {
          // SignatureAPI disabled - generate PDF and finalize
          await signDocument({ documentId, returnUrl: window.location.href });
        }
      } else if (friaId) {
        const payload = buildFRIAPayload(friaFields, createFRIAResponse);
        const result = await completeFRIA(friaId, payload);

        if (!result.success) {
          console.error(
            'FRIA is missing mandatory fields:',
            result.missingFields
          );
          return;
        }

        dispatch(setFRIAStatus('complete'));
        await refreshFRIAState();

        const documentId = createFRIAResponse?.documentId;
        if (!documentId) {
          throw new Error('Missing FRIA document identifier for signing');
        }

        if (result.requiresSignature) {
          const returnUrl = `${window.location.origin}/ai-systems/${systemId}/documentation`;
          const signingUrl = await signDocument({ documentId, returnUrl });
          window.location.assign(signingUrl);
        } else {
          // SignatureAPI disabled - generate PDF and finalize
          await signDocument({ documentId, returnUrl: window.location.href });
        }
      } else if (dpiaId) {
        const payload = buildDPIAPayload(dpiaFields, createDPIAResponse);
        if (!payload) {
          throw new Error('Missing DPIA metadata for completion');
        }
        const result = await completeDPIA(dpiaId, payload);

        if (!result.success) {
          console.error(
            'DPIA is missing mandatory fields:',
            result.missingFields
          );
          return;
        }

        const documentId = createDPIAResponse?.documentId;
        if (!documentId) {
          throw new Error('Missing DPIA document identifier for signing');
        }

        if (result.requiresSignature) {
          const returnUrl = `${window.location.origin}/ai-systems/${systemId}/documentation`;
          const signingUrl = await signDocument({ documentId, returnUrl });
          window.location.assign(signingUrl);
        } else {
          // SignatureAPI disabled - generate PDF and finalize
          await signDocument({ documentId, returnUrl: window.location.href });
        }
      }

      await refreshDocumentationList();
      onClose?.();
    } catch (error) {
      console.error('Failed to mark document as final:', error);
    } finally {
      setFinalizingDraft(false);
    }
  };

  const zoomStyle = useMemo<React.CSSProperties>(
    () => ({
      transform: `scale(${zoom / 100})`,
      transformOrigin: 'top left',
      transition: 'transform 0.2s ease'
    }),
    [zoom]
  );

  // Auto save draft every 30 seconds
  useEffect(() => {
    const autoSave = setInterval(async () => {
      if (finalizingDraft) {
        return;
      }
      try {
        await persistDraftRef.current();
      } catch (error) {
        console.debug('Auto-save skipped:', error);
      }
    }, 30000);

    return () => clearInterval(autoSave);
  }, [finalizingDraft]);

  //za finalizaciju
  useEffect(() => {
    if (title.includes('Annex IV')) {
      const allFieldsFilled = documentationSections
        .flatMap((section) => section.fields)
        .every((field) => {
          const value = annexIVFields[field.label]?.value ?? '';
          return isFilledValue(value);
        });

      if (
        savingDraft ||
        finalizingDraft ||
        !allFieldsFilled ||
        euDeclarationStatus === null ||
        euDeclarationStatus === 'IN_PROGRESS'
      ) {
        setDisableMarkAsFinalButton(true);
      } else {
        setDisableMarkAsFinalButton(false);
      }

      setShowAISuggestion(!allFieldsFilled);
    }

    if (title.includes('EU Declaration')) {
      const isInternalAssessment = documents.some(
        (doc) =>
          doc.category === 'CONFORMITY_ASSESSMENT' &&
          doc.conformityAssessmentType === 'INTERNAL'
      );

      const euDeclarationStaticFields = [
        ...systemAndProviderInformation.fields.filter(
          (field) =>
            !isInternalAssessment ||
            !('optionalForInternal' in field && field.optionalForInternal)
        ),
        ...(isInternalAssessment ? [] : notifiedBodyConformityAssessment.fields)
      ];

      const allStaticFieldsFilled = euDeclarationStaticFields.every((field) => {
        const value = euDeclarationFields[field.label]?.value ?? '';
        return isFilledValue(value);
      });

      const hasConformityContent =
        isInternalAssessment ||
        hasDynamicGroupContent(
          euDeclarationFields[CONFORMITY_STATEMENTS_KEY]?.value
        );

      const hasHarmonisedContent =
        isInternalAssessment ||
        hasDynamicGroupContent(
          euDeclarationFields[HARMONISED_STANDARDS_KEY]?.value
        );

      const isProviderDeclarationChecked =
        euDeclarationFields[PROVIDER_DECLARATION_CHECKED_KEY]?.value ===
          'true' ||
        createEUDeclarationResponse?.declarationStatement?.complied === true;

      const allFieldsFilled =
        allStaticFieldsFilled &&
        hasConformityContent &&
        hasHarmonisedContent &&
        isProviderDeclarationChecked;

      if (savingDraft || finalizingDraft || !allFieldsFilled) {
        setDisableMarkAsFinalButton(true);
      } else {
        setDisableMarkAsFinalButton(false);
      }

      setShowAISuggestion(!allFieldsFilled);
    }

    if (title.includes('FRIA') || title.includes('Fundamental Rights')) {
      const optionalFriaFieldKeys = new Set(['conditionsForDeployment']);

      // Collect all FRIA static fields
      const friaStaticFields = [
        ...friaMetaInformation.fields,
        ...friaSystemDeployerInformation.fields,
        ...useContext.fields,
        ...durationFrequencyOfUse.fields,
        ...affectedPersonsGroups.fields,
        ...humanOversight.fields,
        ...mitigationCorrectiveMeasures.fields,
        ...finalAssessmentDecision.fields
      ].filter((field) => !optionalFriaFieldKeys.has(field.key));

      const allStaticFieldsFilled = friaStaticFields.every((field) => {
        const value = friaFields[field.key]?.value ?? '';
        return isFilledValue(value);
      });

      const hasCheckedOption = (value?: string) => {
        if (!value) return false;

        try {
          const parsed = JSON.parse(value) as Record<string, boolean>;
          return Object.values(parsed).some(Boolean);
        } catch {
          return false;
        }
      };

      const hasSelectedLevel = (levels?: Record<string, boolean>) =>
        levels ? Object.values(levels).some(Boolean) : false;

      const hasCompleteRiskScenarios = (() => {
        const value = friaFields[RISK_SCENARIOS_KEY]?.value;
        if (!value) return false;

        try {
          const scenarios = JSON.parse(value) as {
            riskID?: string;
            fundamentalRight?: string;
            riskDescription?: string;
            affectedPersonsGroups?: string;
            likelihood?: Record<string, boolean>;
            severity?: Record<string, boolean>;
            existingControls?: string;
          }[];
          const filledScenarios = scenarios.filter(
            (scenario) =>
              isFilledValue(scenario.riskID) ||
              isFilledValue(scenario.fundamentalRight) ||
              isFilledValue(scenario.riskDescription) ||
              isFilledValue(scenario.affectedPersonsGroups) ||
              isFilledValue(scenario.existingControls) ||
              hasSelectedLevel(scenario.likelihood) ||
              hasSelectedLevel(scenario.severity)
          );

          return (
            filledScenarios.length > 0 &&
            filledScenarios.every(
              (scenario) =>
                isFilledValue(scenario.riskID) &&
                isFilledValue(scenario.fundamentalRight) &&
                isFilledValue(scenario.riskDescription) &&
                isFilledValue(scenario.affectedPersonsGroups) &&
                hasSelectedLevel(scenario.likelihood) &&
                hasSelectedLevel(scenario.severity) &&
                isFilledValue(scenario.existingControls)
            )
          );
        } catch {
          return false;
        }
      })();

      const hasRoleOfSystem = hasCheckedOption(
        friaFields[ROLE_OF_SYSTEM_KEY]?.value
      );
      const hasVulnerableGroups = hasCheckedOption(
        friaFields[VULNERABLE_GROUPS_KEY]?.value
      );
      const hasFundamentalRightsInScope = hasCheckedOption(
        friaFields[FUNDAMENTAL_RIGHTS_IN_SCOPE_KEY]?.value
      );
      const hasOversightModel = hasCheckedOption(
        friaFields[OVERSIGHT_MODEL_KEY]?.value
      );
      const hasOverallRiskMitigationStatus = isFilledValue(
        friaFields[OVERALL_RISK_MITIGATION_STATUS_KEY]?.value
      );

      // Check if overall decision is selected
      const hasOverallDecision = isFilledValue(
        friaFields[OVERALL_DECISION_KEY]?.value
      );

      const friaEmail = friaFields.email?.value ?? '';
      const hasFriaEmailError =
        friaEmail.length > 0 && !EMAIL_REGEX.test(friaEmail);

      const allFieldsFilled =
        allStaticFieldsFilled &&
        hasRoleOfSystem &&
        hasVulnerableGroups &&
        hasFundamentalRightsInScope &&
        hasCompleteRiskScenarios &&
        hasOversightModel &&
        hasOverallRiskMitigationStatus &&
        hasOverallDecision;

      if (
        savingDraft ||
        finalizingDraft ||
        !allFieldsFilled ||
        hasFriaEmailError
      ) {
        setDisableMarkAsFinalButton(true);
      } else {
        setDisableMarkAsFinalButton(false);
      }

      setShowAISuggestion(!allFieldsFilled);
    }

    if (title.includes('DPIA') || title.includes('Data Protection')) {
      // DPO/Data Protection Contact section is optional ("if applicable"),
      // so it should not block finalization when left empty.
      const optionalDpiaFieldKeys = new Set([
        'dpoContactPerson',
        'dpoEmail',
        'dpiaTitle',
        'referenceId',
        'dpiaDate',
        'dateOfApproval',
        'preparedBy',
        'reviewedBy',
        'consultationWithDataSubjectsOrRepresentatives'
      ]);

      const dpiaRequiredStaticFields = [
        ...controllerAndAssessmentIdentificationFields.fields,
        ...descriptionOfProcessingOperationsAndPurposesFields.fields,
        ...necessityAndProportionalityFields.fields,
        ...consultationAndPriorConsultationFields.fields
      ].filter((field) => !optionalDpiaFieldKeys.has(field.key));

      const allStaticFieldsFilled = dpiaRequiredStaticFields.every((field) => {
        const value = dpiaFields[field.key]?.value ?? '';
        return isFilledValue(value);
      });

      const hasSelectedLevel = (levels?: Record<string, boolean>) =>
        levels ? Object.values(levels).some(Boolean) : false;

      // Check if risk assessment has at least one complete row
      const hasRiskAssessmentContent = (() => {
        const value =
          dpiaFields[RISK_ASSESSMENT_AND_MITIGATION_MEASURES_KEY]?.value;
        if (!value) return false;

        try {
          const risks = JSON.parse(value) as {
            riskID?: string;
            riskDescription?: string;
            affectedDataSubjects?: string;
            likelihoodOfOccurrence?: Record<string, boolean>;
            severity?: Record<string, boolean>;
            initialRiskLevel?: Record<string, boolean>;
            existingControls?: string;
          }[];
          const filledRisks = risks.filter(
            (risk) =>
              isFilledValue(risk.riskID) ||
              isFilledValue(risk.riskDescription) ||
              isFilledValue(risk.affectedDataSubjects) ||
              hasSelectedLevel(risk.likelihoodOfOccurrence) ||
              hasSelectedLevel(risk.severity) ||
              hasSelectedLevel(risk.initialRiskLevel) ||
              isFilledValue(risk.existingControls)
          );

          return (
            filledRisks.length > 0 &&
            filledRisks.every(
              (risk) =>
                isFilledValue(risk.riskID) &&
                isFilledValue(risk.riskDescription) &&
                isFilledValue(risk.affectedDataSubjects) &&
                hasSelectedLevel(risk.likelihoodOfOccurrence) &&
                hasSelectedLevel(risk.severity) &&
                hasSelectedLevel(risk.initialRiskLevel) &&
                isFilledValue(risk.existingControls)
            )
          );
        } catch {
          return false;
        }
      })();

      // Check if measures to address risks has at least one complete row
      const hasMeasuresContent = (() => {
        const value = dpiaFields[MEASURES_TO_ADDRESS_RISKS_KEY]?.value;
        if (!value) return false;

        try {
          const measures = JSON.parse(value) as {
            riskID?: string;
            mitigationMeasures?: string;
            type?: string;
            ownerOrResponsibleRole?: string;
            implementationTimeline?: string;
            residualRisk?: Record<string, boolean>;
          }[];
          const filledMeasures = measures.filter(
            (measure) =>
              isFilledValue(measure.riskID) ||
              isFilledValue(measure.mitigationMeasures) ||
              isFilledValue(measure.type) ||
              isFilledValue(measure.ownerOrResponsibleRole) ||
              isFilledValue(measure.implementationTimeline) ||
              hasSelectedLevel(measure.residualRisk)
          );

          return (
            filledMeasures.length > 0 &&
            filledMeasures.every(
              (measure) =>
                isFilledValue(measure.riskID) &&
                isFilledValue(measure.mitigationMeasures) &&
                isFilledValue(measure.type) &&
                isFilledValue(measure.ownerOrResponsibleRole) &&
                isFilledValue(measure.implementationTimeline) &&
                hasSelectedLevel(measure.residualRisk)
            )
          );
        } catch {
          return false;
        }
      })();

      // Check if overall assessment is selected
      const hasOverallAssessment = isFilledValue(
        dpiaFields[DPIA_OVERALL_ASSESSMENT_KEY]?.value
      );

      // Check if final decision is selected
      const hasFinalDecision = isFilledValue(
        dpiaFields[DPIA_FINAL_DECISION_KEY]?.value
      );

      const allFieldsFilled =
        allStaticFieldsFilled &&
        hasRiskAssessmentContent &&
        hasMeasuresContent &&
        hasOverallAssessment &&
        hasFinalDecision;

      const requiredEmail = dpiaFields.email?.value ?? '';
      const optionalDpoEmail = dpiaFields.dpoEmail?.value ?? '';
      const hasDpiaEmailError =
        (requiredEmail.length > 0 && !EMAIL_REGEX.test(requiredEmail)) ||
        (optionalDpoEmail.length > 0 && !EMAIL_REGEX.test(optionalDpoEmail));

      if (
        savingDraft ||
        finalizingDraft ||
        !allFieldsFilled ||
        hasDpiaEmailError
      ) {
        setDisableMarkAsFinalButton(true);
      } else {
        setDisableMarkAsFinalButton(false);
      }

      setShowAISuggestion(!allFieldsFilled);
    }
  }, [
    finalizingDraft,
    savingDraft,
    title,
    annexIVFields,
    euDeclarationFields,
    friaFields,
    dpiaFields,
    documents,
    euDeclarationStatus,
    createEUDeclarationResponse
  ]);

  return (
    <div className={`flex h-full flex-col bg-white ${className}`}>
      {/* Header */}
      {showHeader && (
        <>
          <div
            className={`flex items-center justify-between ${
              isGenerating ? 'h-12' : ''
            }`}
          >
            <h2 className="text-lg text-zinc-900 font-medium w-96 truncate">
              {title.includes('(Draft)')
                ? title.split('(Draft)').map((part, index, arr) => (
                    <>
                      {part}
                      {index < arr.length - 1 && (
                        <span className="text-gray-600">(Draft)</span>
                      )}
                    </>
                  ))
                : title}
            </h2>
            {isGenerating ? null : (
              <>
                <div className="w-1/3">
                  <ZoomContent zoom={zoom} setZoom={setZoom} />
                </div>
                <div className="flex items-center gap-4 mt-0.5">
                  {content === 'DOCUMENT_PREVIEW' &&
                    previewDocument?.downloadUrl &&
                    showDownloadButton && (
                      <a
                        href={previewDocument.downloadUrl}
                        download={previewDocument.fileName}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <DownloadDocumentEditorIcon />
                      </a>
                    )}
                  {content !== 'DOCUMENT_PREVIEW' &&
                    showDownloadButton &&
                    !isDraftDocument && (
                      <button
                        id="document-editor-download-button"
                        data-testid="document-editor-download-button"
                        onClick={() => {
                          console.log('download');
                        }}
                      >
                        <DownloadDocumentEditorIcon />
                      </button>
                    )}
                  {showCloseButton && onClose && (
                    <button
                      type="button"
                      id="document-editor-close-button"
                      data-testid="document-editor-close-button"
                      onClick={handleClose}
                      className="relative rounded-md text-gray-400 hover:text-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      {XCloseModal()}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          <Divider classes="my-3.5 border-gray-300" />
        </>
      )}

      {isGenerating ? (
        <AIProcessingLoader message="Suggesting Content..." />
      ) : content === 'DOCUMENT_PREVIEW' && previewDocument?.previewUrl ? (
        /* Document Preview */
        <div className="flex-1 overflow-auto mt-5">
          <div
            className="origin-top-left inline-block w-full"
            style={zoomStyle}
          >
            <FileViewer
              fileUrl={previewDocument.previewUrl}
              contentType={previewDocument.contentType}
            />
          </div>
        </div>
      ) : (
        <>
          {/* Content */}
          <div className="flex-1 overflow-auto mt-5">
            <div
              className="origin-top-left inline-block w-full"
              style={zoomStyle}
            >
              {content === 'ANNEX_IV' ? (
                <AnnexIV />
              ) : content === 'FRIA' ? (
                <FRIA />
              ) : content === 'DPIA' ? (
                <DPIA />
              ) : (
                <DeclarationOfConformity />
              )}
            </div>
          </div>

          {/* Footer */}
          {showFooter && (
            <div
              className={`relative flex items-center justify-end border-t border-gray-300 pt-3.5 ${
                showAISuggestion ||
                generationStatus === 'TIMEOUT' ||
                Boolean(generationError)
                  ? 'mt-12'
                  : ''
              }`}
            >
              {(showAISuggestion ||
                generationStatus === 'TIMEOUT' ||
                Boolean(generationError)) && (
                <div className="absolute flex -top-[59px] w-full h-[58px] px-4 pt-3 rounded-t-[24px] bg-[linear-gradient(90deg,#F0F4FD_0.83%,#ADBDF4_100%)]">
                  <AISuggestionIcon />
                  <div className="w-full h-max flex items-center justify-between">
                    <div className="ml-3">
                      {generationError ? (
                        <>
                          <p className="text-sm text-rose-900 font-medium mb-0.5">
                            {generationError}
                          </p>
                          <p className="text-xs leading-3 text-rose-700">
                            AI generation failed. Retry when you are ready.
                          </p>
                        </>
                      ) : generationStatus === 'TIMEOUT' ? (
                        <>
                          <p className="text-sm text-purple-900 font-medium mb-0.5">
                            Still processing...
                          </p>
                          <p className="text-xs leading-3 text-purple-600">
                            Generation is still running. Continue checking the
                            current job status.
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm text-purple-900 font-medium mb-0.5">
                            Smart AI suggestions from your system data.
                          </p>
                          <p className="text-xs leading-3 text-purple-600">
                            Please review the accuracy of generated content
                            before finalization.
                          </p>
                        </>
                      )}
                    </div>
                    {generationStatus === 'TIMEOUT' && currentJobId ? (
                      <button
                        onClick={handleContinueChecking}
                        className="px-[11px] py-[7px] bg-white rounded-full shadow-sm text-xs text-purple-700 font-medium hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:bg-white focus:ring-purple-800 active:ring-purple-800"
                        disabled={!canGenerateSuggestions}
                      >
                        Continue checking
                      </button>
                    ) : (
                      <button
                        onClick={handleSuggestContent}
                        className="px-[11px] py-[7px] bg-white rounded-full shadow-sm text-xs text-purple-700 font-medium hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:bg-white focus:ring-purple-800 active:ring-purple-800"
                        disabled={!canGenerateSuggestions}
                      >
                        {generationError ? 'Retry' : 'Suggest Content'}
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <PrimaryButton
                  onClick={handleMarkAsFinal}
                  icon={DocumentCheckIcon()}
                  disabled={disableMarkAsFinalButton}
                >
                  {finalizingDraft ? 'Marking...' : 'Mark as Final'}
                </PrimaryButton>
              </div>
            </div>
          )}
        </>
      )}
      <Toast
        setShowToast={setShowToast}
        showToast={showToast}
        text="Suggested content generated!"
        position="left-6"
      />
      <Toast
        setShowToast={setShowPermissionToast}
        showToast={showPermissionToast}
        text="Only owner of the AI System has the permission to sign the document"
        position="left-6"
        type="error"
      />
    </div>
  );
}
